import { useEffect, useState } from "react";
import { Card, Form, Button, Badge, InputGroup, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import api from "@/api/api.js";
import { ROLES } from "@/config/roles.js";

const EVENTS = [
  { key: "folio_package_requested", label: "Compra de paquete de folios solicitada" },
  { key: "folio_package_approved", label: "Compra de paquete de folios aprobada (copia)" },
  { key: "invoice_requested_company", label: "Factura de compañía solicitada" },
  { key: "invoice_requested_package", label: "Factura de paquete solicitada" },
  {
    key: "billing_cancellation_requested",
    label: "Cancelación de factura solicitada",
  },
];

const NotificationsAdmin = () => {
  const [recipients, setRecipients] = useState({});
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState({});
  const [pickUser, setPickUser] = useState({});

  const loadEvent = async (key) => {
    const { data } = await api.get("/notification_recipients", {
      params: { event: key },
    });
    setRecipients((prev) => ({ ...prev, [key]: data["hydra:member"] }));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [admins, supers] = await Promise.all([
          api.get("/users", { params: { role: ROLES.ADMIN, itemsPerPage: 100 } }),
          api.get("/users", {
            params: { role: ROLES.SUPER_ADMIN, itemsPerPage: 100 },
          }),
        ]);
        const merged = [
          ...admins.data["hydra:member"],
          ...supers.data["hydra:member"],
        ];
        setAdminUsers(merged);
        await Promise.all(EVENTS.map((e) => loadEvent(e.key)));
      } catch (error) {
        console.error("Error cargando notificaciones:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const addUser = async (key) => {
    const userId = pickUser[key];
    if (!userId) return;
    const user = adminUsers.find((u) => u.id === userId);
    if (!user) return;
    try {
      await api.post("/notification_recipients", {
        event: key,
        email: user.username,
        user: `/api/users/${user.id}`,
      });
      setPickUser((p) => ({ ...p, [key]: "" }));
      await loadEvent(key);
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.["hydra:description"] ||
          "No se pudo agregar el destinatario (¿ya existe?).",
        "error",
      );
    }
  };

  const addEmail = async (key) => {
    const email = (newEmail[key] || "").trim();
    if (!email) return;
    try {
      await api.post("/notification_recipients", { event: key, email });
      setNewEmail((p) => ({ ...p, [key]: "" }));
      await loadEvent(key);
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.["hydra:description"] ||
          "Correo inválido o ya registrado para este evento.",
        "error",
      );
    }
  };

  const toggle = async (key, rec) => {
    await api.patch(
      `/notification_recipients/${rec.id}`,
      { enabled: !rec.enabled },
      { headers: { "Content-Type": "application/merge-patch+json" } },
    );
    await loadEvent(key);
  };

  const remove = async (key, rec) => {
    const ok = await Swal.fire({
      title: "¿Quitar destinatario?",
      text: rec.email,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, quitar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    await api.delete(`/notification_recipients/${rec.id}`);
    await loadEvent(key);
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 p-3">
        <Spinner animation="border" size="sm" />
        <span>Cargando...</span>
      </div>
    );
  }

  return (
    <div className="col">
      <p className="text-muted">
        Por cada evento define quién recibe el correo. Puedes elegir usuarios
        (contadores / jefe de contadores) o agregar correos que no están en el
        sistema. La lista configurada reemplaza por completo a los destinatarios
        anteriores.
      </p>

      {EVENTS.map((ev) => {
        const list = recipients[ev.key] || [];
        return (
          <Card key={ev.key} className="mb-3">
            <Card.Header className="fw-bold">{ev.label}</Card.Header>
            <Card.Body>
              {list.length === 0 ? (
                <div className="text-muted mb-3">
                  Sin destinatarios: este evento no enviará correo.
                </div>
              ) : (
                <ul className="list-group mb-3">
                  {list.map((rec) => (
                    <li
                      key={rec.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span>
                        {rec.email}{" "}
                        {rec.userLabel && (
                          <Badge bg="info" className="ms-1">
                            {rec.userLabel}
                          </Badge>
                        )}
                        {!rec.user && (
                          <Badge bg="secondary" className="ms-1">
                            externo
                          </Badge>
                        )}
                      </span>
                      <span className="d-flex align-items-center gap-3">
                        <Form.Check
                          type="switch"
                          checked={rec.enabled}
                          label={rec.enabled ? "Activo" : "Inactivo"}
                          onChange={() => toggle(ev.key, rec)}
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => remove(ev.key, rec)}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="row g-2">
                <div className="col-md-6">
                  <InputGroup>
                    <Form.Select
                      value={pickUser[ev.key] || ""}
                      onChange={(e) =>
                        setPickUser((p) => ({ ...p, [ev.key]: e.target.value }))
                      }
                    >
                      <option value="">Agregar usuario (ADMIN / SUPER_ADMIN)…</option>
                      {adminUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {(u.userProfile?.name || "") +
                            " " +
                            (u.userProfile?.lastname || "")}{" "}
                          — {u.username}
                        </option>
                      ))}
                    </Form.Select>
                    <Button variant="primary" onClick={() => addUser(ev.key)}>
                      Agregar
                    </Button>
                  </InputGroup>
                </div>
                <div className="col-md-6">
                  <InputGroup>
                    <Form.Control
                      type="email"
                      placeholder="Agregar correo externo…"
                      value={newEmail[ev.key] || ""}
                      onChange={(e) =>
                        setNewEmail((p) => ({ ...p, [ev.key]: e.target.value }))
                      }
                    />
                    <Button variant="outline-primary" onClick={() => addEmail(ev.key)}>
                      Agregar
                    </Button>
                  </InputGroup>
                </div>
              </div>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

export default NotificationsAdmin;
